
/**
 * Module dependencies.
 */

import { Collapse } from '../collapse';
import { Icon } from 'react-components/media';
import { ReactNode, useMemo } from 'react';
import { Text, TextSize } from '../typography';
import { color } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import formErrorIcon from '../assets/svg/16/form-error.svg';
import infoIcon from '../assets/svg/16/info.svg';
import styled, { css } from 'styled-components';
import successIcon from '../assets/svg/16/success.svg';
import warningIcon from '../assets/svg/16/warning.svg';

/**
 * `Type` type.
 */

type Type = 'default' | 'error' | 'info' | 'success' | 'warning';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  hasIcon?: boolean;
  isOpen?: boolean;
  size?: TextSize;
  type?: Type;
};

/**
 * Icons.
 */

const icons = {
  error: formErrorIcon,
  info: infoIcon,
  success: successIcon,
  warning: warningIcon
};

/**
 * `Content` styled component.
 */

const Content = styled.div<{ hasIcon: boolean; type: Type }>`
  border-radius: 8px;
  margin: 4px 0 0;
  padding: 4px 12px;
  transition: ${theme('transitions.defaultTransition')};
  transition-property: background-color, color;

  ${ifProp('hasIcon', css`
    align-items: center;
    display: grid;
    grid-column-gap: 8px;
    grid-template-columns: max-content 1fr;
  `)}

  ${switchProp('type', {
    default: css`
      background-color: ${color('gray200')};
      color: ${color('gray900')};
    `,
    error: css`
      background-color: ${color('danger100')};
      color: ${color('danger')};
    `,
    info: css`
      background-color: ${color('info100')};
      color: ${color('info500')};
    `,
    success: css`
      background-color: ${color('success100')};
      color: ${color('success900')};
    `,
    warning: css`
      background-color: ${color('warning100')};
      color: ${color('warning700')};
    `
  })}
`;

/**
 * `DefaulText` styled component.
 */

const DefaulText = styled(Text).attrs({ size: 'small' })`
  font-size: 14px;
  line-height: 20px;
`;

/**
 * Export `FieldAlert` component.
 */

export function FieldAlert(props: Props): JSX.Element {
  const { children, className, hasIcon = false, isOpen, size, type = 'default' } = props;
  const icon = useMemo(() => {
    return type === 'default' ? null : icons[type];
  }, [type]);

  return (
    <Collapse isOpen={isOpen}>
      <Content
        className={className}
        hasIcon={hasIcon}
        type={type}
      >
        {hasIcon && icon && (
          <Icon
            aria-hidden
            color={'inherit'}
            icon={icon}
            size={'16px'}
          />
        )}

        {size ? (
          <Text size={size}>
            <>
              {children}
            </>
          </Text>
        ) : (
          <DefaulText>
            <>
              {children}
            </>
          </DefaulText>
        )}
      </Content>
    </Collapse>
  );
}
